import { useEffect, useState } from "react";
import { Input, Select, Switch, Table } from "antd";
import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { getAlerts } from "../../api/monitoring";
import CustomerLink from "../shared/CustomerLink";
import { HOLD_LABELS, Pill, STATUS_LABELS, SeverityBadge, dueText, timeAgo } from "./shared";

const columns = [
  { title: "Severity", dataIndex: "severity", key: "severity", render: (v) => <SeverityBadge severity={v} />, width: 90 },
  {
    title: "Alert",
    key: "alert",
    render: (_, a) => (
      <div>
        <div className="font-bold text-[14px]">
          {a.ruleName}
          {a.hitCount > 1 && <span className="text-[12px] font-normal text-[#00000080]"> · seen {a.hitCount} times</span>}
        </div>
        <div className="text-[13px] text-[#00000099]">{a.summary}</div>
        {a.hold?.state && (
          <Pill className={a.hold.state === "held" ? "bg-[#EB5757] text-white" : "bg-[#0000000D] text-[#4F4F4F]"}>
            {HOLD_LABELS[a.hold.state]}
            {a.hold.state !== "held" && a.hold.resolvedBy === "auto" ? " (auto)" : ""}
          </Pill>
        )}
      </div>
    ),
  },
  {
    title: "Customer",
    key: "customer",
    render: (_, a) => (
      <div>
        <CustomerLink userId={a.userId} record={{ user: a.customer }} />
        <div className="text-[12px] text-[#00000080] lowercase">{a.customer?.email}</div>
      </div>
    ),
  },
  {
    title: "Status",
    key: "status",
    render: (_, a) => {
      const due = ["open", "in_review"].includes(a.status) ? dueText(a.dueAt) : null;
      return (
        <div className="text-[13px]">
          <div>{STATUS_LABELS[a.status]}</div>
          {a.assignee && <div className="text-[12px] text-[#00000080]">{a.assignee}</div>}
          {due && <div className={`text-[12px] ${due.late ? "text-[#EB5757]" : "text-[#00000080]"}`}>{due.text}</div>}
        </div>
      );
    },
  },
  { title: "Raised", dataIndex: "createdAt", key: "createdAt", render: (v) => <span className="text-[13px] whitespace-nowrap">{timeAgo(v)}</span> },
];

/**
 * Alert queue with filters. `fixed` filters (e.g. userId) can't be changed.
 */
const AlertsTable = ({ fixed = {}, compact = false, defaultStatus = "active" }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(defaultStatus);
  const [severity, setSeverity] = useState();
  const [held, setHeld] = useState(false);
  const [mine, setMine] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const me = (() => {
    try {
      return decodeURIComponent(document.cookie.match(/(?:^|; )email=([^;]*)/)?.[1] || "");
    } catch {
      return "";
    }
  })();

  const params = {
    page,
    limit: compact ? 10 : 20,
    ...(status !== "all" && { status }),
    ...(severity && { severity }),
    ...(held && { held: "true" }),
    ...(mine && me && { assignee: me }),
    ...(search && { search }),
    ...fixed,
  };
  const { data, isFetching } = useQuery(["monitoring-alerts", params], () => getAlerts(params), {
    keepPreviousData: true,
    refetchInterval: 30000,
  });

  return (
    <div className="flex flex-col gap-3">
      {!compact && (
        <div className="flex gap-3 items-center flex-wrap">
          <Select
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            className="w-[150px]"
            options={[
              { value: "active", label: "Open & in review" },
              ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
              { value: "all", label: "All" },
            ]}
          />
          <Select
            allowClear
            placeholder="Any severity"
            value={severity}
            onChange={(v) => {
              setSeverity(v);
              setPage(1);
            }}
            className="w-[140px]"
            options={["high", "medium", "low"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))}
          />
          <label className="flex items-center gap-2 text-[13px]">
            <Switch size="small" checked={held} onChange={setHeld} /> Held payments only
          </label>
          {me && (
            <label className="flex items-center gap-2 text-[13px]">
              <Switch size="small" checked={mine} onChange={setMine} /> Assigned to me
            </label>
          )}
          {!fixed.userId && (
            <Input.Search
              allowClear
              placeholder="Customer, summary or reference"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-[280px] ml-auto"
            />
          )}
        </div>
      )}
      <Table
        rowKey="_id"
        size={compact ? "small" : "middle"}
        loading={isFetching}
        dataSource={data?.alerts || []}
        columns={fixed.userId ? columns.filter((c) => c.key !== "customer") : columns}
        rowClassName="cursor-pointer hover:bg-gray-50"
        onRow={(a) => ({ onClick: () => navigate(`/monitoring/alerts/${a._id}`) })}
        pagination={{ current: page, pageSize: params.limit, total: data?.total || 0, onChange: setPage, showSizeChanger: false }}
      />
    </div>
  );
};

export default AlertsTable;
